/* eslint-disable camelcase */
/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';

import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Main } from './Main';
import feedOrderSliceReducer from './state/feedOrderSlice';
import loadingReducer from './state/loadingSlice';
import messagesReducer from './state/messagesSlice.js';
import loginSliceReducer, { setImageToken } from './state/loginSlice';
import notificationSliceReducer from './state/notificationsSlice';
import registrationSliceReducer from './state/registrationSlice';
import userProfileSliceReducer from './state/userProfileSlice';

/* Styles */
import './styles/globals.css';
import './styles/type.css';

/* Theme variables */
import { setupIonicReact } from '@ionic/react';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import { BaseApi } from './api/BaseAPI';
import { postUpdateMiddleware } from './middleware/postUpdateMiddleware';
import commentViewSlice from './state/commentViewSlice';
import './theme/variables.css';
import { Preferences } from '@capacitor/preferences';
import { HttpOptions, CapacitorHttp as Http } from '@capacitor/core';
import { getBackendDomain, getBackendURL } from './hooks/useBackendDomain.js';
import { parseCookieHeader } from './api/parseCookieHeader.js';

const store = configureStore({
	reducer: combineReducers({
		[BaseApi.reducerPath]: BaseApi.reducer,
		login: loginSliceReducer,
		commentDeleteState: commentViewSlice,
		registration: registrationSliceReducer,
		userProfile: userProfileSliceReducer,
		notification: notificationSliceReducer,
		feedOrder: feedOrderSliceReducer,
		loading: loadingReducer,
		messages: messagesReducer
	}),
	middleware: getDefaultMiddleware =>
		getDefaultMiddleware().concat(BaseApi.middleware).prepend(postUpdateMiddleware.middleware)
});

// This allows RTK Query to work with refreshOnFocus
setupListeners(store.dispatch);
export type RootState = ReturnType<typeof store.getState>;
setupIonicReact();
const container = document.getElementById('root');
const root = createRoot(container!);

// This is here to close over store.dispatch, there's likely a better way to do this,
// But I don't know it.
export async function HttpRequest(options: HttpOptions): ReturnType<typeof Http.request> {
	const domain = await getBackendDomain();
	const _jwt_token = await Preferences.get({ key: domain + '_jwt_token' });
	const _jwt_refresh_token = await Preferences.get({ key: domain + '_jwt_refresh_token' });
	const _jwt_image_token = await Preferences.get({ key: domain + '_jwt_image_token' });
	const cookies: string[] = [];
	if (_jwt_token && _jwt_token.value) {
		cookies.push(`_jwt_token=${_jwt_token.value}`);
	}
	if (_jwt_refresh_token && _jwt_refresh_token.value) {
		cookies.push(`_jwt_refresh_token=${_jwt_refresh_token.value}`);
	}
	if (_jwt_image_token && _jwt_image_token.value) {
		cookies.push(`_jwt_image_token=${_jwt_image_token.value}`);
	}
	options.headers = {
		...options.headers,
		Cookie: cookies.join('; ')
	};

	options.url = getBackendURL(options.url, domain);
	const response = await Http.request(options);
	if (response.headers['Set-Cookie']) {
		// On web, this will never be called, as you can't see the cookies.
		const cookies = parseCookieHeader(response.headers['Set-Cookie']);
		// The 'await's here are important as it insures that any requests which are triggered
		// by this response see the new jwt tokens.
		if ('_jwt_token' in cookies) {
			await Preferences.set({ key: domain + '_jwt_token', value: cookies._jwt_token.value });
		}
		if ('_jwt_image_token' in cookies) {
			await Preferences.set({ key: domain + '_jwt_image_token', value: cookies._jwt_image_token.value });
			if (store.dispatch) {
				store.dispatch(setImageToken(cookies._jwt_image_token.value));
			}
		}
		if ('_jwt_refresh_token' in cookies) {
			await Preferences.set({ key: domain + '_jwt_refresh_token', value: cookies._jwt_refresh_token.value });
		}
	}
	return response;
}
root.render(
	<StrictMode>
		<Provider store={store}>
			<Main />
		</Provider>
	</StrictMode>
);
